import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  InputBase,
  alpha,
  styled,
  Tabs,
  Tab,
  TablePagination,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Assessment as ReportIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { getdata } from './redux/students';
import axios from 'axios';
import { useTerminology } from './hooks/useTerminology';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function StudentAttendanceReports() {
  const dispatch = useDispatch();
  const students = useSelector((state) => state.students);
  const admin = useSelector((state) => state.admin);
  
  // Get terminology and term options
  const { terminology, termOptions } = useTerminology();

  // State management
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [attendanceStats, setAttendanceStats] = useState({
    totalStudents: 0,
    totalPresent: 0,
    totalAbsent: 0,
    averageAttendance: 0,
    classStats: [],
    dailyStats: []
  });

  // Load students data
  const loadStudents = useCallback(async () => {
    try {
      const response = await axios.post("https://servermain.sisrevolution.com/api/students/getstudentdata/", {
        schoolID: admin.schoolID
      });
      
      if (response.status === 200) {
        dispatch(getdata({ data: response.data }));
        generateReports(response.data);
      }
    } catch (error) {
      showMessage('Error loading students data', 'error');
    }
  }, [admin.schoolID, dispatch]);

  // Generate attendance reports
  const generateReports = useCallback((studentList) => {
    const stats = {
      totalStudents: studentList.length,
      totalPresent: 0,
      totalAbsent: 0,
      averageAttendance: 0,
      classStats: [],
      dailyStats: []
    };

    // Calculate class-wise statistics
    const classGroups = {};
    studentList.forEach(student => {
      if (!classGroups[student.class_]) {
        classGroups[student.class_] = {
          class: student.class_,
          total: 0,
          present: 0,
          absent: 0,
          percentage: 0
        };
      }
      classGroups[student.class_].total++;
    });

    // Calculate attendance for selected date
    studentList.forEach(student => {
      if (student.attendance && student.attendance[selectedTerm]) {
        const todayAttendance = student.attendance[selectedTerm].find(
          att => att.date === selectedDate
        );
        if (todayAttendance) {
          if (todayAttendance.attendance === 'present') {
            stats.totalPresent++;
            classGroups[student.class_].present++;
          } else if (todayAttendance.attendance === 'absent') {
            stats.totalAbsent++;
            classGroups[student.class_].absent++;
          }
        }
      }
    });

    // Calculate percentages
    Object.values(classGroups).forEach(classStat => {
      classStat.percentage = classStat.total > 0 ? 
        Math.round((classStat.present / classStat.total) * 100) : 0;
    });

    stats.classStats = Object.values(classGroups);
    stats.averageAttendance = stats.totalStudents > 0 ? 
      Math.round((stats.totalPresent / stats.totalStudents) * 100) : 0;

    setAttendanceStats(stats);
    setReportData(studentList);
  }, [selectedDate, selectedTerm]);

  // Filter students based on search term and class
  const filterStudents = useCallback(() => {
    let filtered = students;

    if (selectedClass) {
      filtered = filtered.filter(student => student.class_ === selectedClass);
    }

    if (searchTerm) {
      filtered = filtered.filter(student => 
        student.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.othernames.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.ID.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setReportData(filtered);
    generateReports(filtered);
  }, [students, selectedClass, searchTerm, generateReports]);

  // Get attendance status for a student on selected date
  const getAttendanceStatus = (student) => {
    if (!student.attendance || !student.attendance[selectedTerm]) return 'unmarked';
    
    const todayAttendance = student.attendance[selectedTerm].find(
      att => att.date === selectedDate
    );
    
    return todayAttendance ? todayAttendance.attendance : 'unmarked';
  };

  // Calculate student attendance percentage
  const getStudentAttendancePercentage = (student) => {
    if (!student.attendance || !student.attendance[selectedTerm]) return 0;
    
    const termAttendance = student.attendance[selectedTerm];
    const presentDays = termAttendance.filter(att => att.attendance === 'present').length;
    const totalDays = termAttendance.length;
    
    return totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0;
  };

  // Export report to CSV
  const exportToCSV = () => {
    const csvData = reportData.map(student => ({
      'Student ID': student.ID,
      'Name': `${student.lastname} ${student.othernames}`,
      'Class': student.class_,
      'Status': getAttendanceStatus(student),
      'Attendance %': getStudentAttendancePercentage(student)
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_report_${selectedDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showMessage('Report exported successfully', 'success');
  };

  // Print report
  const printReport = () => {
    window.print();
  };

  // Show message
  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setOpenSnackbar(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated students
  const getPaginatedStudents = () => {
    const startIndex = page * rowsPerPage;
    return reportData.slice(startIndex, startIndex + rowsPerPage);
  };

  // Load data on component mount
  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Filter students when search term or class changes
  useEffect(() => {
    filterStudents();
  }, [filterStudents]);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <AppBar position="static" sx={{ mb: 3, background: 'linear-gradient(315deg, #17dddd 0%, #1760d3 74%)' }}>
        <Toolbar>
          <ReportIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Student Attendance Reports
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search students..."
              inputProps={{ 'aria-label': 'search' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Search>
        </Toolbar>
      </AppBar>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {Array.from(new Set(students.map(s => s.class_))).map(cls => (
                  <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>{terminology}</InputLabel>
              <Select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
              >
                <MenuItem value="">Select {terminology}</MenuItem>
                {termOptions.map((option, index) => (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportToCSV}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={printReport}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadStudents}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="attendance tabs">
          <Tab label="Overview" />
          <Tab label="Class Statistics" />
          <Tab label="Student Details" />
          <Tab label="Trends" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Students
                </Typography>
                <Typography variant="h4">
                  {attendanceStats.totalStudents}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Present Today
                </Typography>
                <Typography variant="h4" color="success.main">
                  {attendanceStats.totalPresent}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Absent Today
                </Typography>
                <Typography variant="h4" color="error.main">
                  {attendanceStats.totalAbsent}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Average Attendance
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {attendanceStats.averageAttendance}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Class Statistics Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell align="right">Total Students</TableCell>
                <TableCell align="right">Present</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">Attendance %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendanceStats.classStats.map((classStat) => (
                <TableRow key={classStat.class}>
                  <TableCell component="th" scope="row">
                    <Chip label={classStat.class} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">{classStat.total}</TableCell>
                  <TableCell align="right">{classStat.present}</TableCell>
                  <TableCell align="right">{classStat.absent}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${classStat.percentage}%`}
                      color={classStat.percentage >= 80 ? 'success' : 
                             classStat.percentage >= 60 ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Student Details Tab */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Student ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attendance %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getPaginatedStudents().map((student) => {
                const status = getAttendanceStatus(student);
                const percentage = getStudentAttendancePercentage(student);
                return (
                  <TableRow key={student._id} hover>
                    <TableCell>
                      <Avatar
                        src={`https://thesis1995revolution0268005074.sisrevolution.com/sisrevolution1995/routes/thumbnail/${student.image}`}
                        alt={student.lastname}
                        sx={{ width: 40, height: 40 }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {student.ID}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {student.lastname.toUpperCase()} {student.othernames.toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.class_}
                        size="small"
                        color="default"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.toUpperCase()}
                        color={
                          status === 'present' ? 'success' :
                          status === 'absent' ? 'error' : 'default'
                        }
                        variant={status === 'unmarked' ? 'outlined' : 'filled'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${percentage}%`}
                        color={percentage >= 80 ? 'success' : 
                               percentage >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={reportData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TabPanel>

      {/* Trends Tab */}
      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Attendance Trends
          </Typography>
          <Typography color="textSecondary">
            Trend analysis and charts will be implemented here.
          </Typography>
        </Paper>
      </TabPanel>

      {/* Snackbar for messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={messageType}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
